import React, { useState, useEffect, useRef, useMemo } from "react";
import styled from "styled-components";
import { FiBell, FiInfo, FiCheck, FiCheckCircle } from "react-icons/fi";
import { doc, onSnapshot, setDoc, serverTimestamp } from "firebase/firestore";
import { useAuth } from "../../contexts/AuthContext";
import { db } from "../../firebase";

export default function NotificationCenter() {
    const { user } = useAuth();
    const [isOpen, setIsOpen] = useState(false);
    const [releaseNotes, setReleaseNotes] = useState(null);
    const [banner, setBanner] = useState(null);
    const [seen, setSeen] = useState({});
    const buttonRef = useRef(null);
    const popupRef = useRef(null);

    useEffect(() => {
        const unsub = onSnapshot(
            doc(db, "siteConfig", "releaseNotes"),
            (snap) => {
                if (!snap.exists()) { setReleaseNotes(null); return; }
                const data = snap.data();
                const notes = Array.isArray(data.notes)
                    ? data.notes.map((n) => String(n).trim()).filter(Boolean)
                    : [];
                if (!data.enabled || notes.length === 0) { setReleaseNotes(null); return; }
                setReleaseNotes({
                    releaseId: String(data.releaseId || data.updatedAt?.seconds || "current"),
                    title: data.title || "What's new",
                    description: data.description || "",
                    notes,
                    updatedAt: data.updatedAt,
                });
            },
            (err) => { console.error("Failed to load release notes:", err); setReleaseNotes(null); }
        );
        return () => unsub();
    }, []);

    useEffect(() => {
        const unsub = onSnapshot(
            doc(db, "siteConfig", "maintenance"),
            (snap) => {
                if (!snap.exists()) { setBanner(null); return; }
                const data = snap.data();
                if (!data.enabled) { setBanner(null); return; }
                const now = new Date();
                const start = data.startAt
                    ? (data.startAt.toDate ? data.startAt.toDate() : new Date(data.startAt))
                    : null;
                const end = data.endAt
                    ? (data.endAt.toDate ? data.endAt.toDate() : new Date(data.endAt))
                    : null;
                if (start && now < start) { setBanner(null); return; }
                if (end && now > end) { setBanner(null); return; }
                const updatedSeconds = data.updatedAt?.seconds || 0;
                setBanner({
                    bannerId: String(updatedSeconds || "current"),
                    title: data.title || "Maintenance notice",
                    message: data.message || "",
                    updatedAt: data.updatedAt,
                });
            },
            (err) => { console.error("Failed to load banner:", err); setBanner(null); }
        );
        return () => unsub();
    }, []);

    useEffect(() => {
        if (!user?.email) { setSeen({}); return; }
        const unsub = onSnapshot(
            doc(db, "Users", user.email),
            (snap) => {
                const data = snap.data();
                setSeen(data?.notificationsSeen || {});
            },
            (err) => { console.error("Failed to load seen state:", err); }
        );
        return () => unsub();
    }, [user?.email]);

    const notifications = useMemo(() => {
        const items = [];
        if (releaseNotes) {
            items.push({
                id: `release:${releaseNotes.releaseId}`,
                type: "release",
                icon: null,
                title: releaseNotes.title,
                description: releaseNotes.description,
                bullets: releaseNotes.notes,
            });
        }
        if (banner) {
            items.push({
                id: `banner:${banner.bannerId}`,
                type: "banner",
                icon: FiInfo,
                title: banner.title,
                description: banner.message,
                bullets: null,
            });
        }
        return items;
    }, [releaseNotes, banner]);

    const unreadCount = useMemo(
        () => notifications.filter((n) => !seen[n.id]).length,
        [notifications, seen]
    );

    useEffect(() => {
        const handle = (e) => {
            if (
                popupRef.current &&
                !popupRef.current.contains(e.target) &&
                buttonRef.current &&
                !buttonRef.current.contains(e.target)
            ) {
                setIsOpen(false);
            }
        };
        if (isOpen) document.addEventListener("mousedown", handle);
        return () => document.removeEventListener("mousedown", handle);
    }, [isOpen]);

    const markSeen = async (id) => {
        if (!user?.email) return;
        try {
            await setDoc(
                doc(db, "Users", user.email),
                { notificationsSeen: { [id]: serverTimestamp() } },
                { merge: true }
            );
        } catch (e) {
            console.error("Failed to mark notification as seen:", e);
        }
    };

    const markAllSeen = async () => {
        if (!user?.email) return;
        const updates = {};
        notifications.forEach((n) => { if (!seen[n.id]) updates[n.id] = serverTimestamp(); });
        if (Object.keys(updates).length === 0) return;
        try {
            await setDoc(
                doc(db, "Users", user.email),
                { notificationsSeen: updates },
                { merge: true }
            );
        } catch (e) {
            console.error("Failed to mark all as seen:", e);
        }
    };

    if (!user) return null;

    return (
        <Wrapper>
            <BellButton
                ref={buttonRef}
                onClick={() => setIsOpen((o) => !o)}
                aria-label={`Notifications${unreadCount ? ` (${unreadCount} unread)` : ""}`}
                aria-haspopup="menu"
                aria-expanded={isOpen}
            >
                <FiBell size={20} />
                {unreadCount > 0 && <Badge>{unreadCount > 9 ? "9+" : unreadCount}</Badge>}
            </BellButton>
            {isOpen && (
                <Popup ref={popupRef} role="menu">
                    <PopupHeader>
                        <PopupTitle>Notifications</PopupTitle>
                        {unreadCount > 0 && (
                            <MarkAllButton onClick={markAllSeen}>Mark all as read</MarkAllButton>
                        )}
                    </PopupHeader>
                    {notifications.length === 0 ? (
                        <EmptyState>You're all caught up</EmptyState>
                    ) : (
                        <ItemList>
                            {notifications.map((n) => {
                                const Icon = n.icon;
                                const isUnread = !seen[n.id];
                                return (
                                    <Item key={n.id} $unread={isUnread}>
                                        {Icon && (
                                            <ItemIcon $type={n.type}>
                                                <Icon size={18} />
                                            </ItemIcon>
                                        )}
                                        <ItemBody>
                                            <ItemTitleRow>
                                                <ItemTitle>{n.title}</ItemTitle>
                                                {isUnread && <UnreadDot aria-label="Unread" />}
                                            </ItemTitleRow>
                                            {n.description && (
                                                <ItemDescription>{n.description}</ItemDescription>
                                            )}
                                            {n.bullets && n.bullets.length > 0 && (
                                                <BulletList>
                                                    {n.bullets.map((b, i) => (
                                                        <Bullet key={i}>
                                                            <FiCheckCircle size={14} />
                                                            <span>{b}</span>
                                                        </Bullet>
                                                    ))}
                                                </BulletList>
                                            )}
                                            {isUnread && (
                                                <DismissButton
                                                    onClick={() => markSeen(n.id)}
                                                    aria-label={`Mark "${n.title}" as read`}
                                                >
                                                    <FiCheck size={14} /> Mark as read
                                                </DismissButton>
                                            )}
                                        </ItemBody>
                                    </Item>
                                );
                            })}
                        </ItemList>
                    )}
                </Popup>
            )}
        </Wrapper>
    );
}

const Wrapper = styled.div`
    position: relative;
    display: inline-block;
`;

const BellButton = styled.button`
    all: unset;
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    color: #4b5563;
    background: #f3f4f6;
    cursor: pointer;
    transition: background-color 0.2s, color 0.2s, transform 0.2s;

    &:hover {
        background: #e5e7eb;
        color: #111827;
    }
    &:active { transform: scale(0.95); }
`;

const Badge = styled.span`
    position: absolute;
    top: 4px;
    right: 4px;
    min-width: 16px;
    height: 16px;
    padding: 0 4px;
    border-radius: 8px;
    background: #ef4444;
    color: #fff;
    font-size: 10px;
    font-weight: 700;
    line-height: 16px;
    text-align: center;
    box-sizing: border-box;
`;

const Popup = styled.div`
    position: absolute;
    top: 100%;
    right: 0;
    margin-top: 8px;
    width: min(380px, 92vw);
    max-height: 70vh;
    overflow-y: auto;
    background: #fff;
    border-radius: 8px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
    z-index: 200;
    text-align: left;
    animation: fadeIn 0.2s ease-out;

    @keyframes fadeIn {
        from { opacity: 0; transform: translateY(-8px); }
        to   { opacity: 1; transform: translateY(0); }
    }

    @media (max-width: 600px) {
        width: min(340px, 92vw);
    }
`;

const PopupHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 16px;
    border-bottom: 1px solid #f1f5f9;
    position: sticky;
    top: 0;
    background: #fff;
    z-index: 1;
`;

const PopupTitle = styled.h3`
    margin: 0;
    font-size: 15px;
    font-weight: 700;
    color: #111827;
    font-family: Inter, sans-serif;
`;

const MarkAllButton = styled.button`
    all: unset;
    cursor: pointer;
    color: #9333ea;
    font-size: 12px;
    font-weight: 600;
    font-family: Inter, sans-serif;

    &:hover { color: #7e22ce; text-decoration: underline; }
`;

const EmptyState = styled.div`
    padding: 32px 16px;
    text-align: center;
    color: #6b7280;
    font-size: 14px;
    font-family: Inter, sans-serif;
`;

const ItemList = styled.ul`
    list-style: none;
    margin: 0;
    padding: 4px 0;
`;

const Item = styled.li`
    display: flex;
    gap: 12px;
    padding: 12px 16px;
    border-bottom: 1px solid #f8fafc;
    background: ${(props) => (props.$unread ? "#f5f3ff" : "transparent")};

    &:last-child { border-bottom: none; }
`;

const ItemIcon = styled.div`
    flex: 0 0 auto;
    width: 32px;
    height: 32px;
    border-radius: 8px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: ${(props) => (props.$type === "release" ? "#ecfeff" : "#eff6ff")};
    color: ${(props) => (props.$type === "release" ? "#0891b2" : "#3b82f6")};
`;

const ItemBody = styled.div`
    flex: 1;
    min-width: 0;
`;

const ItemTitleRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const ItemTitle = styled.div`
    font-size: 14px;
    font-weight: 700;
    color: #111827;
    font-family: Inter, sans-serif;
    line-height: 1.3;
    word-break: break-word;
`;

const UnreadDot = styled.span`
    flex: 0 0 auto;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #9333ea;
`;

const ItemDescription = styled.p`
    margin: 4px 0 0;
    font-size: 13px;
    color: #4b5563;
    line-height: 1.45;
    text-align: left;
    font-family: Inter, sans-serif;
    word-break: break-word;
`;

const BulletList = styled.ul`
    list-style: none;
    margin: 8px 0 0;
    padding: 0;
    display: grid;
    gap: 6px;
`;

const Bullet = styled.li`
    display: grid;
    grid-template-columns: 16px minmax(0, 1fr);
    gap: 8px;
    align-items: start;
    text-align: left;
    font-size: 12.5px;
    color: #374151;
    line-height: 1.4;
    font-family: Inter, sans-serif;

    svg {
        margin-top: 2px;
        color: #16a34a;
        flex: 0 0 auto;
    }
    span { word-break: break-word; }
`;

const DismissButton = styled.button`
    all: unset;
    margin-top: 10px;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 4px 10px;
    border-radius: 6px;
    background: #ede9fe;
    color: #6d28d9;
    font-size: 12px;
    font-weight: 600;
    font-family: Inter, sans-serif;
    cursor: pointer;
    transition: background-color 0.2s;

    &:hover { background: #ddd6fe; }
`;
